import React from "react";
import { getProcessWithRelations } from "../services/processService";
import { getOptimizationData } from "../services/processOptimizationService";
import { getBpmnPng, getOptimizedBpmnPng } from "../services/bpmnService";

export default function useOptimizationData() {
  const [selectedId, setSelectedId] = React.useState(null);
  const [optimizationData, setOptimizationData] = React.useState(null);
  // The CMS process record behind this run. Already fetched below for the
  // task-count guard, so it is kept rather than discarded: consumers need its
  // company to scope anything company-owned (e.g. the jobs a role may be
  // reassigned to) instead of reaching for an unscoped list.
  const [processMeta, setProcessMeta] = React.useState(null);
  const [optimizing, setOptimizing] = React.useState(false);
  const [showMinTasksModal, setShowMinTasksModal] = React.useState(false);
  const [bpmnImages, setBpmnImages] = React.useState({
    current: null,
    optimized: null,
  });
  const bpmnUrlsRef = React.useRef({ current: null, optimized: null });
  const [bpmnLoading, setBpmnLoading] = React.useState(false);
  const [bpmnZoom, setBpmnZoom] = React.useState(1);
  const [bpmnOffset, setBpmnOffset] = React.useState({ x: 0, y: 0 });
  const bpmnDragRef = React.useRef({
    active: false,
    startX: 0,
    startY: 0,
    baseX: 0,
    baseY: 0,
  });
  const [errorModal, setErrorModal] = React.useState({
    open: false,
    title: "",
    message: "",
  });
  const requestSeqRef = React.useRef(0);

  React.useEffect(() => {
    return () => {
      if (bpmnUrlsRef.current.current)
        URL.revokeObjectURL(bpmnUrlsRef.current.current);
      if (bpmnUrlsRef.current.optimized)
        URL.revokeObjectURL(bpmnUrlsRef.current.optimized);
    };
  }, []);

  const resetSelection = React.useCallback(() => {
    requestSeqRef.current += 1;
    setOptimizationData(null);
    setProcessMeta(null);
    if (bpmnUrlsRef.current.current)
      URL.revokeObjectURL(bpmnUrlsRef.current.current);
    if (bpmnUrlsRef.current.optimized)
      URL.revokeObjectURL(bpmnUrlsRef.current.optimized);
    bpmnUrlsRef.current = { current: null, optimized: null };
    setBpmnImages({ current: null, optimized: null });
    setBpmnZoom(1);
    setBpmnOffset({ x: 0, y: 0 });
  }, []);

  const loadBpmnImages = React.useCallback(async (processId, requestId, scenarioSpec) => {
    if (!processId) return;
    setBpmnLoading(true);
    try {
      // First render on a cold server can fail once and succeed right after —
      // one automatic retry replaces the user's toggle-back-and-forth ritual.
      const withRetry = (fn) =>
        fn().catch(
          () => new Promise((r) => setTimeout(r, 1500)).then(fn).catch(() => null),
        );
      const [currentUrl, optimizedUrl] = await Promise.all([
        withRetry(() => getBpmnPng(processId, { skipValidation: true })),
        // Pin to the scenario ACTIVE at call time (defaults to the top-ranked
        // scenario on first load) — without this the Optimized image always used
        // the backend's own auto-detect elimination, ignoring which scenario card
        // was selected.
        withRetry(() => getOptimizedBpmnPng(processId, { skipValidation: true, scenarioSpec })),
      ]);

      if (requestId !== requestSeqRef.current) {
        if (currentUrl) URL.revokeObjectURL(currentUrl);
        if (optimizedUrl) URL.revokeObjectURL(optimizedUrl);
        return;
      }

      if (!currentUrl && !optimizedUrl) {
        throw new Error(
          "No BPMN image available for current or optimized process",
        );
      }

      if (bpmnUrlsRef.current.current)
        URL.revokeObjectURL(bpmnUrlsRef.current.current);
      if (bpmnUrlsRef.current.optimized)
        URL.revokeObjectURL(bpmnUrlsRef.current.optimized);
      bpmnUrlsRef.current = { current: currentUrl, optimized: optimizedUrl };
      setBpmnImages({ current: currentUrl, optimized: optimizedUrl });
      setBpmnZoom(1);
      setBpmnOffset({ x: 0, y: 0 });
    } catch (e) {
      if (requestId !== requestSeqRef.current) return;
      console.error("Failed to load BPMN images", e);
      setErrorModal({
        open: true,
        title: "Failed to Load BPMN",
        message: e?.message || "Unknown error",
      });
    } finally {
      if (requestId === requestSeqRef.current) setBpmnLoading(false);
    }
  }, []);

  // Re-fetch ONLY the optimized PNG for the given scenario — used when the analyst
  // switches the ACTIVE scenario card after the initial load. "Current" never
  // depends on the scenario, so it is deliberately left alone (no flicker/refetch).
  const refreshOptimizedImage = React.useCallback(async (processId, scenarioSpec, jobOverrides) => {
    if (!processId) return;
    const requestId = ++requestSeqRef.current;
    try {
      const url = await getOptimizedBpmnPng(processId, { skipValidation: true, scenarioSpec, jobOverrides });
      if (requestId !== requestSeqRef.current) {
        URL.revokeObjectURL(url);
        return;
      }
      if (bpmnUrlsRef.current.optimized) URL.revokeObjectURL(bpmnUrlsRef.current.optimized);
      bpmnUrlsRef.current = { ...bpmnUrlsRef.current, optimized: url };
      setBpmnImages((prev) => ({ ...prev, optimized: url }));
    } catch (e) {
      // Best-effort: keep whatever image is already on screen rather than blank it.
      console.error("Failed to refresh optimized BPMN image for the active scenario", e);
    }
  }, []);

  const optimize = React.useCallback(
    // `options.includeSubprocessRedesigns` mirrors the RL and BPR panels' opt-in
    // toggle — off by default, since it costs one extra full greedy run per
    // sub-process and forces the slower synchronous endpoint (see the service).
    async (processId, goalWeights, options = {}) => {
      if (!processId) return;
      const requestId = requestSeqRef.current + 1;
      requestSeqRef.current = requestId;
      setSelectedId(processId);
      setOptimizing(true);
      setOptimizationData(null);

      try {
        const proc = await getProcessWithRelations(processId).catch(() => null);
        if (requestId === requestSeqRef.current) setProcessMeta(proc);
        let tasksCount = 0;
        let confident = false;
        const wf = Array.isArray(proc?.workflow) ? proc.workflow : null;
        if (wf) {
          const ids = wf.map((w) => w?.task_id).filter(Boolean);
          if (ids.length > 0) {
            tasksCount = new Set(ids).size;
            confident = true;
          } else if (wf.length > 0) {
            tasksCount = wf.length;
            confident = true;
          }
        }
        if (!confident && Array.isArray(proc?.tasks)) {
          tasksCount = proc.tasks.length;
          confident = true;
        }
        if (!confident && Array.isArray(proc?.process_tasks)) {
          tasksCount = proc.process_tasks.length;
          confident = true;
        }
        if (confident && tasksCount < 3) {
          if (requestId !== requestSeqRef.current) return;
          setShowMinTasksModal(true);
          setOptimizing(false);
          return;
        }

        const data = await getOptimizationData(processId, goalWeights, {
          includeSubprocessRedesigns: !!options.includeSubprocessRedesigns,
        });
        if (!data) {
          throw new Error("No data received from server");
        }

        if (requestId !== requestSeqRef.current) return;
        setOptimizationData(data);
        // Render the Optimized image for whichever scenario the optimize response
        // marks ACTIVE (the top-ranked one by default) — NOT the backend's own
        // independent auto-detect default, which can disagree with it (e.g. the
        // top scenario is "Resources only" / eliminates nothing, but auto-detect
        // still finds an NVA step worth removing).
        const activeSpec = (data.scenarios || []).find((s) => s.is_active)?.bpmn_spec;
        await loadBpmnImages(processId, requestId, activeSpec);
      } catch (e) {
        if (requestId !== requestSeqRef.current) return;
        console.error("Failed to load optimization data", e);
        const errorMessage = e?.message || "Unknown error";
        setErrorModal({
          open: true,
          title: "Failed to Load Optimization",
          message: `${errorMessage}\nPlease ensure the backend server is running and the API endpoint is accessible.`,
        });
      } finally {
        if (requestId === requestSeqRef.current) setOptimizing(false);
      }
    },
    [loadBpmnImages],
  );

  return {
    selectedId,
    setSelectedId,
    optimizationData,
    processMeta,
    // Exposed so the page can switch the ACTIVE scenario: swapping redesign_flow +
    // redesign_report keeps every tab consistent (they all derive from these two).
    setOptimizationData,
    optimizing,
    showMinTasksModal,
    setShowMinTasksModal,
    bpmnImages,
    refreshOptimizedImage,
    bpmnLoading,
    bpmnZoom,
    setBpmnZoom,
    bpmnOffset,
    setBpmnOffset,
    bpmnDragRef,
    errorModal,
    setErrorModal,
    resetSelection,
    optimize,
  };
}
